import { createReducer, INITIAL_STATE } from '@shared/redux';
import { getReportConfig } from '@shared/api/MetricsApi';
import { DEFAULT_METRICS } from '@shared/constants/MetricsTypeConstants';
import { ColorConstants } from '@shared/constants';
import { UPDATE_OBJECT_SELECTED } from '@domain/_redux/pareto-ads-app-redux/action/actionTypes';
import ToastNotification, {
    showToast,
    TOAST_TYPE,
} from '@shared/components/messages-and-errors/ToastsNotifications';

import { LOAD_STATE } from '@shared/axios';
import { SUPPRESSED_WARNINGS } from '@domain/dashboard/metrics-report/metrics-report.constants';
import { PERIOD_FIELDS } from '@shared/constants/Segmented.constants';
import { getSegmentedContextNotification } from '../segmented/segmented-container.utils';
import Routes from '../../router/routes.constants';
import { addCalculatedMetrics } from './metrics-report.utils';

export const GET_REPORT_FOR_CUSTOMER_ID_REQUEST_CALLED = 'GET_REPORT_FOR_CUSTOMER_ID_REQUEST_CALLED';
export const GET_REPORT_FOR_CUSTOMER_ID_REQUEST_SUCCEEDED = 'GET_REPORT_FOR_CUSTOMER_ID_REQUEST_SUCCEEDED';
export const GET_REPORT_FOR_CUSTOMER_ID_REQUEST_FAILED = 'GET_REPORT_FOR_CUSTOMERS_ID_REQUEST_FAILED';
export const RESET_SELECTED_METRICS_TO_INITIAL_VALUE = 'RESET_SELECTED_METRICS_TO_INITIAL_VALUE';

export const CHANGE_SEGMENTATION = 'CHANGE_SEGMENTATION';
export const CHANGE_METRIC_POSITION = 'CHANGE_METRIC_POSITION';
export const TOGGLE_METRIC_ACTIVE = 'TOGGLE_METRIC_ACTIVE';

export const GET_INITIAL_METRICS = '@dashboard/metrics-report/GET_INITIAL_METRICS';
export const SET_INITIAL_METRICS = '@dashboard/metrics-report/SET_INITIAL_METRICS';

export const METRICS_REPORT_CALLER = '@dashboard/metrics-report/METRICS_REPORT_CALLER';
export const PREVIOUS_PERIOD_METRICS_REPORT_CALLER = '@dashboard/metrics-report/PREVIOUS_PERIOD_METRICS_REPORT_CALLER';
export const EMPTY_REPORT = '@dashboard/metrics-report/EMPTY_REPORT';
export const METRICS_REPORT_LOADING = '@dashboard/metrics-report/METRICS_REPORT_LOADING';

export const getSelectedMetrics = state => ({
    firstMetric: state.dashboard.metricsReport.firstMetric,
    secondMetric: state.dashboard.metricsReport.secondMetric,
});

export const callWarning = (action) => {
    const { warning } = action?.payload?.data || {};
    const warningIsSuppressed = SUPPRESSED_WARNINGS.includes(warning);
    const shouldWarn = warning && !warningIsSuppressed;

    if (shouldWarn) {
        showToast(
            getSegmentedContextNotification(action.payload),
            TOAST_TYPE.WARNING,
            { autoClose: 60000, toastId: warning, height: 300 },
            ToastNotification,
            Routes.SETTINGS,
        );
    }
};

export const metricsReportCaller = () => ({
    type: METRICS_REPORT_CALLER,
    payload: {},
});

export const previousPeriodMetricsReportCaller = () => ({
    type: PREVIOUS_PERIOD_METRICS_REPORT_CALLER,
    payload: {},
});

export const metrisReportLoading = () => ({
    type: METRICS_REPORT_LOADING,
    payload: {},
});

export const emptyReport = () => ({
    type: EMPTY_REPORT,
    payload: {},
});

export const getMetricsReport = ({
    customerType,
    customerId,
    startDate,
    endDate,
    segmentation,
    campaigns,
    periodSegmentation,
    conversionType,
}) => ({
    type: GET_REPORT_FOR_CUSTOMER_ID_REQUEST_CALLED,
    actionTypes: {
        success: GET_REPORT_FOR_CUSTOMER_ID_REQUEST_SUCCEEDED,
        fail: GET_REPORT_FOR_CUSTOMER_ID_REQUEST_FAILED,
    },
    periodSegmentation,
    config: getReportConfig({
        customerType, customerId, startDate, endDate, segmentation, campaigns, conversionType,
    }),
    meta: {
        onAxiosFinally: callWarning,
    },
});

export const changeSegmentation = segmentation => ({
    type: CHANGE_SEGMENTATION,
    segmentation,
});

export const toggleMetricActive = metricPosition => ({
    type: TOGGLE_METRIC_ACTIVE,
    metricPosition,
});

export const changeMetricPosition = (metric, metricPosition) => ({
    type: CHANGE_METRIC_POSITION,
    metric,
    metricPosition,
});

export const resetToInitialMetrics = () => ({
    type: RESET_SELECTED_METRICS_TO_INITIAL_VALUE,
    payload: {},
});

export const setInitialMetrics = metrics => ({
    type: SET_INITIAL_METRICS,
    payload: metrics,
});

export const getInitialMetrics = () => ({
    type: GET_INITIAL_METRICS,
    payload: {},
});

const dataInitialState = () => ({
    total: {},
    segmented: [],
    loadState: LOAD_STATE.LOADING,
});


export const setInitialComparisonMetric = (filters) => {
    const comparisonMetric = !!filters.comparisonMetric;
    if (comparisonMetric) {
        return filters.comparisonMetric;
    }
    return getInitialMetricsReporComparisonMetric(filters.metrics.firstMetric.metric);
};

const getInitialMetricsReporComparisonMetric = firstMetric => ({
    firstMetric: {
        isSelected: true,
        metric: firstMetric,
    },
    secondMetric: {
        isSelected: true,
        metric: {
            ...PERIOD_FIELDS.comparison_period,
            label: `Comparing ${firstMetric.label}`,
            color: ColorConstants.FUN_GREEN,
            comparisonMetric: firstMetric.label,
        },
    },
});

const initialState = {
    data: dataInitialState(),
    comparisonData: dataInitialState(),
    error: null,
    segmentation: 'Date',
    firstMetric: {
        isSelected: true,
        metric: { ...DEFAULT_METRICS.clicks, color: ColorConstants.FUN_GREEN },
    },
    secondMetric: {
        isSelected: true,
        metric: { ...DEFAULT_METRICS.cost, color: ColorConstants.FUN_BLUE },
    },
    comparisonMetric: getInitialMetricsReporComparisonMetric(
        { ...DEFAULT_METRICS.clicks, color: ColorConstants.FUN_GREEN },
    ),
};

const resetDatasToInitialState = state => ({
    ...state,
    data: dataInitialState(),
    comparisonData: dataInitialState(),
    error: null,
});

const resetPeriodDatasToInitialState = (state, action) => ({
    ...state,
    [action.periodSegmentation]: dataInitialState(),
    error: null,
});

export const metricsReportReducer = createReducer({
    [INITIAL_STATE]: initialState,
    [GET_REPORT_FOR_CUSTOMER_ID_REQUEST_CALLED]: resetPeriodDatasToInitialState,
    [GET_REPORT_FOR_CUSTOMER_ID_REQUEST_SUCCEEDED]: (state, action) => ({
        ...state,
        [action.requestAction.periodSegmentation]: {
            loadState: LOAD_STATE.SUCCESS,
            ...addCalculatedMetrics(action.payload),
        },
        error: null,
        retryRequestsCount: 0,
    }),
    [GET_REPORT_FOR_CUSTOMER_ID_REQUEST_FAILED]: (state, action) => ({
        ...state,
        [action.requestAction.periodSegmentation]: {
            ...dataInitialState(),
            loadState: LOAD_STATE.FAIL,
        },
        error: action.payload,
    }),
    [CHANGE_SEGMENTATION]: (state, action) => ({
        ...state,
        segmentation: action.segmentation,
    }),
    [CHANGE_METRIC_POSITION]: (state, action) => ({
        ...state,
        [action.metricPosition]: {
            metric: action.metric,
            isSelected: true,
        },
    }),
    [TOGGLE_METRIC_ACTIVE]: (state, action) => ({
        ...state,
        [action.metricPosition]: {
            isSelected: !state[action.metricPosition].isSelected,
        },
    }),
    [UPDATE_OBJECT_SELECTED]: state => ({
        ...state,
        selectedCampaigns: [],
    }),
    [SET_INITIAL_METRICS]: (state, action) => ({
        ...state,
        firstMetric: action.payload.firstMetric,
        secondMetric: action.payload.secondMetric,
        comparisonMetric: action.payload.comparisonMetric,
    }),
    [RESET_SELECTED_METRICS_TO_INITIAL_VALUE]: () => initialState,
    [EMPTY_REPORT]: () => ({
        ...initialState,
        data: {
            ...initialState.data,
            loadState: LOAD_STATE.SUCCESS,
        },
        comparisonData: {
            ...initialState.data,
            loadState: LOAD_STATE.SUCCESS,
        },
    }),
    [METRICS_REPORT_LOADING]: resetDatasToInitialState,
});
